import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Inbox, Search, Filter, User, Clock, CheckCircle, AlertTriangle,
  MessageSquare, ChevronRight, Send, Loader2, BarChart3, Users,
  RefreshCw, X, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

/**
 * Admin Cases - Case queue management for support team
 */
export default function AdminCases() {
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCase, setActiveCase] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    unassigned: false
  });

  useEffect(() => {
    loadCases();
    loadStats();
  }, [filters]);

  const loadCases = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.unassigned) params.append('unassigned_only', 'true');
      
      const { data } = await api.get(`/admin/cases/queue?${params}`);
      setCases(data);
    } catch (error) {
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get('/admin/cases/stats');
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const loadCaseDetail = async (caseId) => {
    try {
      const { data } = await api.get(`/admin/cases/${caseId}`);
      setActiveCase(data);
    } catch (error) {
      toast.error('Failed to load case');
    }
  };

  const assignToMe = async (caseId) => {
    try {
      await api.post(`/admin/cases/${caseId}/assign`);
      toast.success('Case assigned to you');
      loadCases();
      if (activeCase?.id === caseId) {
        loadCaseDetail(caseId);
      }
    } catch (error) {
      toast.error('Failed to assign case');
    }
  };

  const sendReply = async () => {
    if (!activeCase || !replyMessage.trim()) return;

    setSending(true);
    try {
      await api.post(`/admin/cases/${activeCase.id}/respond`, {
        message: replyMessage
      });
      toast.success('Reply sent');
      setReplyMessage('');
      loadCaseDetail(activeCase.id);
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const resolveCase = async () => {
    if (!activeCase) return;

    try {
      await api.post(`/admin/cases/${activeCase.id}/resolve`);
      toast.success('Case resolved');
      setActiveCase(null);
      loadCases();
      loadStats();
    } catch (error) {
      toast.error('Failed to resolve case');
    }
  };

  const closeCase = async () => {
    if (!activeCase) return;

    try {
      await api.post(`/admin/cases/${activeCase.id}/close`);
      toast.success('Case closed');
      setActiveCase(null);
      loadCases();
      loadStats();
    } catch (error) {
      toast.error('Failed to close case');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'ai_handling': 'bg-purple-100 text-purple-800',
      'escalated': 'bg-amber-100 text-amber-800',
      'human_handling': 'bg-orange-100 text-orange-800',
      'resolved': 'bg-green-100 text-green-800',
      'closed': 'bg-slate-100 text-slate-600'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'bg-red-100 text-red-800',
      'high': 'bg-orange-100 text-orange-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-slate-100 text-slate-800';
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl pb-20 lg:pb-6">
        {/* Header - mobile responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-3xl font-display font-bold text-slate-900">Case Queue</h1>
            <p className="text-sm text-slate-600">Manage cases and respond as Denise</p>
          </div>
          <Button onClick={() => { loadCases(); loadStats(); }} variant="outline" className="w-full sm:w-auto">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards - mobile responsive */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">Needs Attention</p>
                    <p className="text-lg sm:text-2xl font-bold text-amber-600">{stats.needs_attention}</p>
                  </div>
                  <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-amber-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">AI Resolution</p>
                    <p className="text-lg sm:text-2xl font-bold text-green-600">{stats.ai_resolution_rate}%</p>
                  </div>
                  <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-green-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">Avg Resolution</p>
                    <p className="text-lg sm:text-2xl font-bold">{stats.avg_resolution_hours}h</p>
                  </div>
                  <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-slate-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">Satisfaction</p>
                    <p className="text-lg sm:text-2xl font-bold">{stats.avg_satisfaction || 'N/A'}</p>
                  </div>
                  <Users className="w-6 h-6 sm:w-8 sm:h-8 text-slate-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Cases List */}
          <div className="lg:col-span-1">
            <Card className="h-[400px] sm:h-[600px] flex flex-col">
              <CardHeader className="border-b pb-3 p-3 sm:p-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base sm:text-lg">Queue</CardTitle>
                  <span className="text-xs sm:text-sm text-slate-500">{cases.length} cases</span>
                </div>
                
                {/* Filters - stack on mobile */}
                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(f => ({...f, status: e.target.value}))}
                    className="text-xs px-2 py-1.5 border rounded flex-1"
                  >
                    <option value="">All Status</option>
                    <option value="new">New</option>
                    <option value="escalated">Escalated</option>
                    <option value="human_handling">In Progress</option>
                  </select>
                  <select
                    value={filters.priority}
                    onChange={(e) => setFilters(f => ({...f, priority: e.target.value}))}
                    className="text-xs px-2 py-1.5 border rounded flex-1"
                  >
                    <option value="">All Priority</option>
                    <option value="urgent">Urgent</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </CardHeader>
              
              <CardContent className="flex-1 overflow-y-auto p-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </div>
                ) : cases.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>No cases in queue</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {cases.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => loadCaseDetail(c.id)}
                        className={`w-full p-3 text-left hover:bg-slate-50 transition-colors ${
                          activeCase?.id === c.id ? 'bg-primary/5 border-l-2 border-primary' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-slate-900 truncate">{c.subject}</p>
                            <p className="text-xs text-slate-500 truncate">
                              {c.user_name || c.user_email}
                            </p>
                            <div className="flex items-center gap-1.5 mt-1">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${getStatusColor(c.status)}`}>
                                {c.status?.replace('_', ' ')}
                              </span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${getPriorityColor(c.priority)}`}>
                                {c.priority}
                              </span>
                            </div>
                          </div>
                          <span className="text-[10px] text-slate-400 whitespace-nowrap">
                            {formatTime(c.created_at)}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Case Detail */}
          <div className="lg:col-span-2">
            <Card className="h-[400px] sm:h-[600px] flex flex-col">
              {activeCase ? (
                <>
                  <CardHeader className="border-b p-3 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <Button variant="ghost" size="sm" onClick={() => setActiveCase(null)} className="p-2">
                          <ArrowLeft className="w-4 h-4" />
                        </Button>
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-base sm:text-lg truncate">{activeCase.subject}</CardTitle>
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(activeCase.status)}`}>
                              {activeCase.status?.replace('_', ' ')}
                            </span>
                            <span className="text-xs text-slate-500 truncate">
                              {activeCase.user_info?.name}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {!activeCase.assigned_to && (
                          <Button size="sm" onClick={() => assignToMe(activeCase.id)} className="text-xs">
                            Assign
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={resolveCase} className="text-xs">
                          Resolve
                        </Button>
                        <Button size="sm" variant="ghost" onClick={closeCase} className="text-xs">
                          Close
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  {/* Messages */}
                  <CardContent className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 bg-slate-50">
                    {activeCase.messages?.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[90%] sm:max-w-[80%] ${msg.role === 'user' 
                          ? 'bg-slate-200 rounded-2xl rounded-br-md' 
                          : 'bg-white border rounded-2xl rounded-bl-md'
                        } px-3 sm:px-4 py-2 sm:py-3 shadow-sm`}>
                          <div className="flex items-center gap-2 mb-1 text-xs text-slate-500">
                            <span className="font-medium">
                              {msg.role === 'user' ? activeCase.user_info?.name || 'User' : 'Denise'}
                            </span>
                            <span>{formatTime(msg.timestamp)}</span>
                          </div>
                          <p className="text-xs sm:text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                  
                  {/* Reply */}
                  <div className="p-3 sm:p-4 border-t bg-white">
                    <p className="text-xs text-slate-500 mb-2">Replying as Denise</p>
                    <div className="flex gap-2">
                      <Textarea
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Type your response..."
                        className="flex-1 resize-none text-sm"
                        rows={2}
                      />
                      <Button onClick={sendReply} disabled={sending || !replyMessage.trim()} size="sm">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <CardContent className="flex-1 flex items-center justify-center">
                  <div className="text-center px-4">
                    <Inbox className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-base sm:text-lg font-medium text-slate-900 mb-1">Select a case</h3>
                    <p className="text-sm text-slate-500">Click on a case to view details</p>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
